import Guid from "common/values/guid/guid";
import {Change} from "diff";

export interface TextChangeCollectionJSON {
  changes: TextChangeJSON[];
}

export interface TextChangeJSON {
  diff: Change;
  isResolved: boolean;
}

export class TextChangeCollection {
  public changes: TextChange[] = [];

  public get allChangesResolved(): boolean {
    return this.changes.every((change) => change.isResolved);
  }

  public toJSON(): TextChangeCollectionJSON {
    return {
      changes: this.changes.map((change) => change.toJSON()),
    };
  }

  public static fromJSONs(jsons: Array<TextChangeJSON>): TextChangeCollection {
    const collection = new TextChangeCollection();
    jsons.forEach((json) => {
      collection.changes.push(TextChange.fromJSON(json, collection));
    });
    return collection;
  }

  public clone(): TextChangeCollection {
    const collection = new TextChangeCollection();
    collection.changes = this.changes.map((change) => change.clone(collection));
    return collection;
  }
}

export default class TextChange {
  public readonly id = Guid.generate();
  public diff: Change;
  public parent: TextChangeCollection;

  constructor(
    parent: TextChangeCollection | undefined,
    diff: Change,
  ) {
    this.parent = parent ?? new TextChangeCollection();
    this.diff = diff;
  }

  public get index(): number {
    return this.parent.changes.indexOf(this);
  }
  public get isResolved(): boolean {
    return !this.diff.added && !this.diff.removed;
  }
  public get previousChange(): TextChange | undefined {
    return this.parent.changes[this.index - 1];
  }
  public get nextChange(): TextChange | undefined {
    return this.parent.changes[this.index + 1];
  }
  public get isModified(): boolean {
    return (
      ((this.diff.removed && this.nextChange?.diff.added) ||
        (this.previousChange?.diff.removed && this.diff?.added)) ??
      false
    );
  }
  public get isAdded(): boolean {
    return (
      (this.diff.added &&
        !this.previousChange?.diff.added &&
        !this.previousChange?.diff.removed) ??
      false
    );
  }
  public get isRemoved(): boolean {
    return (
      (this.diff.removed &&
        !this.previousChange?.diff.added &&
        !this.previousChange?.diff.removed) ??
      false
    );
  }

  public accept() {
    const updatedChanges = [...this.parent.changes];
    const previousIndex = this.index - 1;

    // resolve previous change
    if (this.isModified && this.previousChange?.isModified) {
      const newChange = new TextChange(
        this.parent,
        {
          added: false,
          removed: false,
          value: this.diff.value,
          count: this.diff.count,
        }
      );
      updatedChanges.splice(previousIndex, 2, newChange);
    }

    // resolve next change
    if (this.isModified && this.nextChange?.isModified) {
      const nextChange = new TextChange(
        this.parent,
        {
          added: false,
          removed: false,
          value: this.nextChange.diff.value,
          count: this.nextChange.diff.count,
        }
      );
      updatedChanges.splice(this.index, 2, nextChange);
    }

    // resolve current change
    if (!this.isModified && this.isRemoved) {
      updatedChanges.splice(this.index, 1);
    }

    if (!this.isModified && this.isAdded) {
      const newChange = new TextChange(
        this.parent,
        {
          added: false,
          removed: false,
          value: this.diff.value,
          count: this.diff.count,
        }
      );
      updatedChanges.splice(this.index, 1, newChange);
    }

    this.parent.changes = updatedChanges;
  }

  public reject() {
    const updatedChanges = [...this.parent.changes];
    const previousIndex = this.index - 1;

    // resolve previous change
    if (this.isModified && this.previousChange?.isModified) {
      const newChange = new TextChange(
        this.parent,
        {
          added: false,
          removed: false,
          value: this.previousChange?.diff.value,
          count: this.previousChange?.diff.count,
        }
      );
      updatedChanges.splice(previousIndex, 2, newChange);
    }

    // resolve next change
    if (this.isModified && this.nextChange?.isModified) {
      const nextChange = new TextChange(
        this.parent,
        {
          added: false,
          removed: false,
          value: this.diff.value,
          count: this.diff.count,
        }
      );
      updatedChanges.splice(this.index, 2, nextChange);
    }

    // resolve current change
    if (!this.isModified && this.isRemoved) {
      const newChange = new TextChange(
        this.parent,
        {
          added: false,
          removed: false,
          value: this.diff.value,
          count: this.diff.count,
        }
      );
      updatedChanges.splice(this.index, 1, newChange);
    }

    if (!this.isModified && this.isAdded) {
      updatedChanges.splice(this.index, 1);
    }

    this.parent.changes = updatedChanges;
  }

  public editChange(newValue: string) {
    const updatedChanges = [...this.parent.changes];
    const previousIndex = this.index - 1;

    const newChange = new TextChange(
      this.parent,
      {
        added: false,
        removed: false,
        value: newValue,
        count: this.diff.count,
      }
    );

    if (this.isModified && this.previousChange?.isModified) {
      updatedChanges.splice(previousIndex, 2, newChange);
    } else if (this.isModified && this.nextChange?.isModified) {
      updatedChanges.splice(this.index, 2, newChange);
    } else if (!this.isModified) {
      updatedChanges.splice(this.index, 1, newChange);
    }

    this.parent.changes = updatedChanges;
  }

  public toJSON(): TextChangeJSON {
    return {
      diff: this.diff,
      isResolved: this.isResolved
    };
  }

  static fromJSON(json: TextChangeJSON, parent?: TextChangeCollection): TextChange {
    return new TextChange(parent, json.diff);
  }

  public clone(parent: TextChangeCollection): TextChange {
    return new TextChange(parent, this.diff);
  }
}
